import { getClientTimezone } from '@chilipiper/date-time'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Key } from 'react-aria'
import { Box, Flex, Text } from '../../../core-components'
import {
  DynamicItem,
  DynamicSimpleItem,
  selectItemPublicToDynamicItem,
} from '../base-select/dynamicItemUtils'
import { ItemData } from '../base-select/itemData.types'
import { SelectItemPublic } from '../select-item/SelectItem'
import { isVisible } from '../select-list/useSelectListData'
import { CurrentTimeDisplay } from './CurrentTimeDisplay'
import { areasOrder, getTimeZoneOptions } from './helpers'

type ListProps = {
  hasTime?: boolean
  locale?: string
  search?: string
  value?: string
  size: 'M' | 'S'
}

type ListData = {
  disabledKeys: Array<string>
  existingValues: Set<string>
  itemData: Record<Key, ItemData>
  options: Array<DynamicItem>
}

export const useTimezoneListData = ({ hasTime, locale, search, value, size }: ListProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [listData, setListData] = useState<ListData>({
    existingValues: new Set(),
    itemData: {},
    options: [],
    disabledKeys: [],
  })

  const timeZoneOptionsByArea = useMemo(() => {
    const timezoneOptions = getTimeZoneOptions(locale, true)
    const timezonesByArea = new Map<string, typeof timezoneOptions>()

    areasOrder.forEach(areaName => {
      timezonesByArea.set(areaName, [])
    })

    timezoneOptions.forEach(timezoneOption => {
      if (!timezoneOption.area) {
        return
      }

      const areaOptions = timezonesByArea.get(timezoneOption.area) ?? []
      areaOptions.push(timezoneOption)
      timezonesByArea.set(timezoneOption.area, areaOptions)
    })

    return timezonesByArea
  }, [locale])

  const getListData = useCallback(() => {
    const clientTimezone = getClientTimezone()
    const existingValues: ListData['existingValues'] = new Set()
    const itemData: ListData['itemData'] = {}
    const options: ListData['options'] = []
    const disabledKeys: string[] = []

    for (const area of areasOrder) {
      const areaTimezones = timeZoneOptionsByArea.get(area) as ReturnType<typeof getTimeZoneOptions>
      const sectionItems: DynamicSimpleItem[] = []
      areaTimezones.forEach(timeZoneOption => {
        const { labelComponents = {}, label, value: optionValue } = timeZoneOption
        const { cities, name, offset, abbreviation } = labelComponents
        existingValues.add(optionValue)
        const biggestCity = cities?.split(',')[0]
        const hasCity = Boolean(biggestCity)

        const timezoneDisplayName = size === 'M' ? name : abbreviation

        const item = (
          <SelectItemPublic key={optionValue} value={optionValue} textValue={label}>
            <Flex alignItems='center' gap={1} minWidth={0}>
              {(size === 'M' || value === optionValue) && (
                <Box flexShrink={0} w={13}>
                  <Text color='text/body-moderate' textStyle='form-value'>
                    {offset ? `GMT${offset}` : 'GMT±??:??'}
                  </Text>
                </Box>
              )}
              <Text
                textStyle='form-value'
                display='block'
                overflow='hidden'
                lineHeight={8}
                my={-1}
                whiteSpace='nowrap'
                textOverflow='ellipsis'
              >{`${timezoneDisplayName ?? optionValue}${hasCity ? ' - ' : ''}${
                biggestCity ?? ''
              }`}</Text>
              {hasTime && (
                <Box flexShrink={0} ml='auto'>
                  <CurrentTimeDisplay locale={locale} timezone={optionValue} />
                </Box>
              )}
            </Flex>
          </SelectItemPublic>
        )
        const isHidden = !isVisible(search, label)

        const dynamicItemProps = selectItemPublicToDynamicItem(item, 'item')
        itemData[optionValue] = {
          ...dynamicItemProps,
          isHidden,
          textValue: item.props.textValue ?? item.props.children,
        }

        if (!search && clientTimezone === optionValue) {
          options.unshift(dynamicItemProps)
          return
        }
        if (isHidden) {
          disabledKeys.push(optionValue)
        }

        sectionItems.push(dynamicItemProps)
      })

      options.push(
        selectItemPublicToDynamicItem({ title: area, children: sectionItems }, 'section')
      )
    }

    return {
      existingValues,
      itemData,
      options,
      disabledKeys,
    }
  }, [hasTime, locale, search, size, timeZoneOptionsByArea, value])

  useEffect(() => {
    const updateListData = () => {
      setListData(getListData())
      setIsLoading(false)
    }

    setIsLoading(true)
    let deferredHandleId: number | ReturnType<typeof setTimeout> = -1

    if ('requestIdleCallback' in window) {
      deferredHandleId = requestIdleCallback(updateListData)
    } else {
      deferredHandleId = setTimeout(updateListData, 0)
    }

    return () => {
      if ('requestIdleCallback' in window) {
        cancelIdleCallback(deferredHandleId as number)
      } else {
        clearTimeout(deferredHandleId)
      }
    }
  }, [getListData])

  return { isLoading, listData }
}
